// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import Image from 'next/image';
import Link from 'next/link';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 18, 23, 0.45);
  z-index: 999;
  animation: ${fadeIn} 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const CartDropdown = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100dvh;
  width: min(420px, 100vw);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  z-index: 1000;
  box-shadow: -12px 0 40px rgba(17, 18, 23, 0.18);
  animation: ${slideIn} 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const Title = styled.h5`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.ink};

  span {
    margin-left: 6px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.inkMuted};
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const RemoveAllButton = styled.button`
  border: none;
  background: none;
  padding: 4px 0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.otelRed};
    text-decoration: underline;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.border};
    cursor: wait;
  }
`;

export const RemoveItemButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  border: none;
  border-radius: 50%;
  background: none;
  font-size: 18px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.otelRed};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.border};
    cursor: wait;
  }
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.ink};
  }
`;

export const ItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px;
`;

export const Item = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

export const ItemLink = styled(Link)`
  flex-shrink: 0;
  text-decoration: none;
  border-radius: 8px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.grafanaOrange};
    outline-offset: 2px;
  }
`;

export const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
  min-width: 0;

  > span {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
  }
`;

export const ItemNameLink = styled(Link)`
  text-decoration: none;
  border-radius: 4px;

  &:hover p {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.grafanaOrange};
    outline-offset: 2px;
  }
`;

export const ItemImageWell = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
`;

export const ItemImage = styled(Image).attrs({
  width: 60,
  height: 60,
})`
  object-fit: contain;
`;

export const ItemName = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.ink};
`;

export const QuantityStepper = styled.div`
  display: inline-flex;
  align-items: center;
  height: 26px;
  margin-top: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

export const StepButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 100%;
  border: none;
  background: none;
  font-size: 13px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.border};
    cursor: wait;
  }
`;

export const StepValue = styled.span`
  min-width: 18px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.ink};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 100%;
  text-align: center;
`;

export const EmptyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};

  svg {
    stroke: ${({ theme }) => theme.colors.grafanaOrange};
  }
`;

export const EmptyCart = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.ink};
`;

export const EmptyHint = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const StartShoppingLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 26px;
  border-radius: 999px;
  background: linear-gradient(93deg, #f46800 0%, #ff8833 100%);
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ink};
  text-decoration: none;

  &:hover {
    filter: brightness(1.06);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ink};
    outline-offset: 2px;
  }
`;

export const ErrorNote = styled.p`
  margin: 0;
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 500;
  color: #b42318;
  background: #fef3f2;
  border-top: 1px solid #fecdca;
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};

  > span {
    font-size: 17px;
    font-weight: 700;
  }
`;

export const ShippingNote = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const CheckoutLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 8px;
  padding: 13px 28px;
  min-height: 48px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.ink};
  background: linear-gradient(93deg, #f46800 0%, #ff8833 100%);
  transition: box-shadow 0.15s ease, transform 0.15s ease, filter 0.15s ease;

  &:hover {
    filter: brightness(1.06);
    box-shadow: 0 4px 16px rgba(244, 104, 0, 0.35);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ink};
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    filter: grayscale(0.4);
    opacity: 0.55;
    cursor: wait;
    box-shadow: none;
  }
`;
